import * as React from 'react'
import { Theme } from '@mui/material/styles'
import { Actions, ParentHeader as Container } from './styling'
import { useSelectFunnelStagesByType } from '@user/store/salesFunnel/hooks/useSelectFunnelStagesByType'
import { useActions } from './hooks'
import { AlertDialogSlide, MoreButton } from '@sales-center/components'
import { LeadParentHeaderProps } from './typings'
import {
  LeadAttachmentsButton,
  LeadAttachmentsDrawer,
} from './LeadAttachmentsDrawer'
import { NotesButton, NotesDrawer } from './NotesDrawer'
import { LeadStatusSelector } from './LeadStatusSelector'
import { ReassignDialog } from './ReassignDialog'
import { ReOpenLeadDialog } from '../ReOpenDialog/ReOpenLeadDialog'
import { ConvertToOpportunityButton } from './ConvertToOpportunity'

import { ActivitiesManager } from '@user/features/Activity/ActivitiesManager'
import { ENTITY_TYPES } from '@sales-center/common'

import { NextActionDate } from '@user/components/EntityCards/atoms/NextActionDate/NextActionDate'
import Stack from '@mui/material/Stack'
import { useSelector } from 'react-redux'
import { selectCurrentUser } from '@user/store/authorization'

const styles = {
  actionButton: {
    backgroundColor: (theme: Theme) => theme.palette.common.white,
    marginRight: (theme: Theme) => theme.spacing(1),
    '&hover:': {
      backgroundColor: 'ffffff',
    },
  },
}

export const ParentHeader: React.FC<LeadParentHeaderProps> = (props) => {
  const {
    detailPage,
    lead: { salesFunnelId, id, salesTeamId, assigneeId, nextActionDate },
    canUpdate,
  } = props

  const salesFunnelStages = useSelectFunnelStagesByType(salesFunnelId, 'Lead')
  const { isAdmin } = useSelector(selectCurrentUser)

  const {
    isDisqualified,
    notesState: { notesMenuItem, ...notesState },
    deleteState: { deleteMenuItem, ...deleteState },
    attachmentsState: { attachmentsMenuItem, ...attachmentsState },
    reopenState: { reopenMenuItem, ...reopenState },
    reassignState: { reassignMenuItem, ...reassignState },
  } = useActions({ ...props, salesFunnelStages })

  return (
    <>
      <Container detailPage={detailPage}>
        <Stack direction="row" spacing={2}>
          <ActivitiesManager
            entityId={id}
            entityType={ENTITY_TYPES.Lead}
            salesTeamId={salesTeamId}
            ownerId={assigneeId}
          />
          <NextActionDate
            id={id}
            nextActionDate={nextActionDate}
            entity={ENTITY_TYPES.Lead}
            disabled={!canUpdate}
          />
        </Stack>

        {/* ---------- actions ---------- */}
        <Actions>
          <LeadStatusSelector
            {...props}
            isDisqualified={isDisqualified}
            salesFunnelStages={salesFunnelStages}
          />

          {detailPage && (
            <>
              <NotesButton
                {...props}
                {...notesState}
                sx={styles.actionButton}
              />

              <LeadAttachmentsButton
                {...attachmentsState}
                sx={styles.actionButton}
              />
            </>
          )}

          <ConvertToOpportunityButton
            {...props}
            salesFunnelStages={salesFunnelStages}
          />

          <MoreButton
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            dataTestId={`leadMoreBtn-${id}`}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            options={[
              { ...notesMenuItem, dataTestId: `leadCardNotesOption-${id}` },
              { ...deleteMenuItem, dataTestId: `leadCardDeleteOption-${id}` },
              {
                ...reassignMenuItem,
                dataTestId: `leadCardReassignOption-${id}`,
              },
              { ...reopenMenuItem, dataTestId: `leadCardReopenOption-${id}` },
              {
                ...attachmentsMenuItem,
                dataTestId: `leadCardAttachmentsOption-${id}`,
              },
            ]}
          />
        </Actions>
      </Container>
      {/* DIALOGS */}
      <NotesDrawer {...props} {...notesState} />
      <LeadAttachmentsDrawer {...props} {...attachmentsState} />
      <AlertDialogSlide {...deleteState} />
      {reopenState.salesTeamLoadingStatus === 'success' && (
        <ReOpenLeadDialog {...reopenState} />
      )}
      <ReassignDialog
        {...props}
        {...reassignState}
        showInactiveUsers={isDisqualified && isAdmin}
      />
    </>
  )
}
ParentHeader.displayName = 'ParentHeader'
